import React from "react";
import moment from "moment";
import { Modal, Card, Row, Typography, Tooltip, Button, Alert } from "antd";
import PropTypes from "prop-types";
import { getGCSUrl } from "../../../../services/Common";
import "./StartMCQ.modal.css";
import { withStyles } from "@material-ui/core/styles";
import roundCheck from "../../../../assets/img/round-check_circle-24px.svg";
import {
  ClockIcon,
  RecommendedMcqIcon,
} from "../../../components/common/CustomIcon";
import { TestState } from "../constants";
import { WarningOutlined } from "@material-ui/icons";

const { Title } = Typography;

const useStyles = (theme) => ({
  roundCheck: {
    width: "18px",
    height: "18px",
    background:
      "transparent url(" +
      roundCheck +
      ") 0% 0% no-repeat padding-box !important",
    opacity: "1",
  },

  completedButton: {
    border: "none",
    color: "#66CC80",
    width: "84px",
    height: "36px",
    padding: "0px",
    pointerEvent: "none",
    letterSpacing: "0.56px",
    textDecoration: "none",
    outline: "none",
    fontSize: "0.9em",
  },

  buttonItemSpacing: {
    marginLeft: "10px",
    textAlign: "right",
  },

  medianMCQTime: {
    textAlign: 'left',
    font: 'Regular 12px/16px',
    letterSpacing: '0px',
    color: '#AAAAAA',
    opacity: 1
  }
});

/**
 * This function returns the complete URL for the icon.
 * The previous version of the API provided only the icon name however, the recent version of the API
 * provides the entire URL. When `mcq_use_apiv1` flag is 100% rolled out, remove the if checks for `mcq_use_apiv1`
 * flag and directly return either the cover_image or the icon value based on what is present.
 * @param {*} activeItem The item that is being rendered
 * @param {*} mcq_use_apiv1 The value of flag
 * @returns The entire URL for the icon
 */
function getIcon(activeItem, mcq_use_apiv1) {
  let GCSIcon = undefined;

  if (activeItem.cover_image) {
    if (mcq_use_apiv1) {
      GCSIcon = activeItem.cover_image;
    } else {
      GCSIcon = `${getGCSUrl()}/cover/${activeItem.cover_image}`;
    }
  } else {
    if (mcq_use_apiv1) {
      GCSIcon = activeItem.icon;
    } else {
      GCSIcon = `${getGCSUrl()}/icon/${activeItem.icon}`;
    }
  }

  return GCSIcon;
}

function StartMCQModal(props) {
  const {
    activeItem,
    disabledStart,
    classes,
    mcq_use_apiv1,
    use_mcq_proctoring,
  } = props;
  const { CompletedOrTime, challenge_duration } = activeItem || {};

  const FooterContent = () => 
    !use_mcq_proctoring ? (
      <div className="turing-ui-v2-start-mcq-modal-actions no-margin-top">
        <span className="timeEstimate">
          <ClockIcon />
          Max. Time {challenge_duration} min
        </span>
        {/* Remove <StartButton> component when `mcq_use_apiv1` is rolled out to 100% of users*/}
        {/* Keep <StartButtonV1> component when `mcq_use_apiv1` is rolled out to 100% of users*/}
        {mcq_use_apiv1 ? (
          <StartButtonV1
            disabledStart={disabledStart}
            activeItem={activeItem}
            classes={classes}
          />
        ) : (
          <StartButton
            disabledStart={disabledStart}
            activeItem={activeItem}
            classes={classes}
          />
        )}
        {CompletedOrTime && <CompletedOrTime estimated />}
      </div>
    ) : (
      <div className="turing-ui-v2-start-mcq-modal-actions no-margin-top">
        <span className="timeEstimate">
          <ClockIcon />
          Max. Time {challenge_duration} min
        </span>
        <StartButtonV1
          disabledStart={disabledStart}
          activeItem={activeItem}
          classes={classes}
          use_mcq_proctoring={use_mcq_proctoring}
        />
        {CompletedOrTime && <CompletedOrTime estimated />}
      </div>
    );

  return (
    <Modal
      visible={!!activeItem}
      onCancel={activeItem && activeItem.close}
      footer={<FooterContent />}
      width={"720px"}
      className="turing-ui-v2-mcq-modal"
      bodyStyle={{ padding: 0, margin: 0, maxHeight: "80vh", overflow: "auto" }}
      destroyOnClose
      centered
    >
      {activeItem ? (
        !use_mcq_proctoring ? (
          <div className="turing-ui-v2-start-mcq-modal-container">
            <Card className="details-card">
              <div className="turing-ui-v2-start-mcq-modal-title">
                <img
                  src={getIcon(activeItem, mcq_use_apiv1)}
                  alt="Icon"
                  className="mcq-icon"
                />
                <Title level={4}>
                  {activeItem.challenge_name}
                </Title>
              </div>
              {activeItem.recommended ? (
                <div className="turing-ui-v2-recommended">
                  Recommended based on your
                  <br /> Work History and skills
                </div>
              ) : null}
              <div
                className="challenge-description ql-editor"
                dangerouslySetInnerHTML={{
                  __html: activeItem.challenge_description,
                }}
              />
            </Card>
          </div>
        ) : (
          <div className="turing-ui-v2-start-mcq-modal-container">
            <Card
              title="TURING QUIZZES"
              className="details-card"
              headStyle={{ fontWeight: "bold" }}
              bodyStyle={{ padding: "24px 32px", color: "#212121" }}
            >
              <div className="turing-ui-v2-start-mcq-modal-title">
                <img
                  src={getIcon(activeItem, mcq_use_apiv1)}
                  alt="Icon"
                  style={{ maxWidth: 48, marginBottom: 16 }}
                />
                <Title level={4}>
                  {activeItem.challenge_name}
                </Title>
              </div>
              {activeItem.recommended ? (
                <div className="turing-ui-v2-recommended">
                  <RecommendedMcqIcon />
                </div>
              ) : null}
              <div
                className="challenge-description"
                style={{ marginBottom: 24 }}
              >
                This quiz is your next step towards {activeItem.challenge_name}{" "}
                positions in Silicon Valley, and other top U.S companies.
              </div>
              <div className="fair-assessment-policy-description">
                <h4 style={{ fontWeight: "bold", color: "#212121" }}>
                  Fair assessment policy
                </h4>

                <p>
                  To ensure fairness, we track browser activity and submission
                  patterns to detect any signs of cheating during the test
                  attempt.
                </p>

                <div>
                  <p style={{ marginBottom: 0 }}>
                    During the test attempt, DO NOT:
                  </p>
                  <ul>
                    <li>Open new browser tabs or windows</li>
                    <li>Capture screenshots</li>
                    <li>Conduct any online searches for answers</li>
                    <li>
                      Utilize any tools to answer questions (such as ChatGPT,
                      browser plugins, etc.)
                    </li>
                  </ul>

                  <Alert
                    message=""
                    description="Violation of this policy can result in a temporary or permanent ban from the platform."
                    type="warning"
                    showIcon
                    style={{
                      padding: "9px 9px 9px 44px",
                      height: 40,
                      display: "flex",
                      alignItems: "center",
                    }}
                    icon={
                      <WarningOutlined
                        style={{ fontSize: 24, top: "4px", left: "15px" }}
                      />
                    }
                  />
                </div>
              </div>
            </Card>
          </div>
        )
      ) : null}
    </Modal>
  );
}

function StartMCQModalV2(props) {
  const { activeItem, disabledStart, classes } = props;
  const { CompletedOrTime, duration } = activeItem || {};

  const FooterContent = () =>  (
    <div className="turing-ui-v2-start-mcq-modal-actions no-margin-top">
      <span className="timeEstimate">
        <ClockIcon />
        Max. Time {duration} min
      </span>
      <StartButtonV1
        disabledStart={disabledStart}
        activeItem={activeItem}
        classes={classes}
      />
      {CompletedOrTime && <CompletedOrTime estimated />}
    </div>
  );

  return (
    <Modal
      visible={!!activeItem}
      onCancel={activeItem && activeItem.close}
      footer={<FooterContent />}
      width={"720px"}
      className="turing-ui-v2-mcq-modal"
      bodyStyle={{ padding: 0, margin: 0, maxHeight: "80vh", overflow: "auto" }}
      destroyOnClose
      centered
    >
      {activeItem ? (
        <div className="turing-ui-v2-start-mcq-modal-container">
          <Card className="details-card">
            <div className="turing-ui-v2-start-mcq-modal-title">
              <img
                src={
                  getIcon(activeItem, true)
                }
                alt="Icon"
                className="mcq-icon"
              />
              <Title level={4}>{activeItem.name}</Title>
            </div>
            <div
              className="challenge-description ql-editor"
              dangerouslySetInnerHTML={{
                __html: activeItem.description
              }}
            />
          </Card>
        </div>
      ) : null}
    </Modal>
  );
}

const UsuallyCompleted = ({ activeItem, classes, median_time }) => {
  if (activeItem.submit_time && median_time && median_time > 0) {
    return (
      <Typography className={classes.medianMCQTime}>
        Usually completed in {median_time} min
        {median_time > 1 ? "s" : ""}
      </Typography>
    );
  }
  return null;
};

UsuallyCompleted.propTypes = {
  activeItem: PropTypes.object,
  median_time: PropTypes.any,
  classes: PropTypes.object,
};

const StartButton = ({ activeItem, classes, disabledStart }) => {
  const continueBtn =
    activeItem.timeLeft > 0 ? (
      <Button
        icon="caret-right"
        disabled={disabledStart}
        type="primary"
        onClick={activeItem.proceed}
      >
        Continue
      </Button>
    ) : null;

  const startTestBtn =
    !activeItem.submit_time && activeItem.timeLeft === 0 ? (
      <Button
        icon="caret-right"
        disabled={disabledStart}
        type="primary"
        onClick={activeItem.proceed}
      >
        Start Quiz
      </Button>
    ) : null;

  const RetakeTestBtn =
    activeItem.submit_time &&
    !activeItem.one_shot &&
    activeItem.timeLeft <= 0 ? (
      <Button
        icon="reload"
        disabled={disabledStart}
        type="primary"
        onClick={activeItem.proceed}
      >
        Retake test
      </Button>
    ) : null;

  const completedBtn =
    activeItem.passed !== 1 &&
    activeItem.submit_time &&
    activeItem.one_shot &&
    activeItem.timeLeft <= 0 ? (
      <Tooltip
        placement="top"
        title={moment(activeItem.submit_time).format("MMM D YYYY, hh:mmA")}
      >
        <Button className={`completed ${classes.completedButton}`}>
          <span className={classes.roundCheck} />
          <span className={classes.buttonItemSpacing}>Completed</span>
        </Button>
      </Tooltip>
    ) : null;

  if (!continueBtn && !startTestBtn && !RetakeTestBtn && !completedBtn)
    return null;

  return (
    <Row className="start-button-actions">
      {continueBtn}
      {startTestBtn}
      {RetakeTestBtn}
      {completedBtn}
    </Row>
  );
};

const StartButtonV1 = ({ activeItem, classes, disabledStart, use_mcq_proctoring }) => {
  // Shows a Continue button when the mcq is in progress
  const continueBtn =
    activeItem.state === TestState.IN_PROGRESS ? (
      <Button
        icon="caret-right"
        disabled={disabledStart}
        type="primary"
        onClick={activeItem.proceed}
      >
        Continue
      </Button>
    ) : null;

  // Shows a Start button when the mcq is not attempted
  const startTestBtn =
    activeItem.state === TestState.TODO ? (
      <Button
        icon="caret-right"
        disabled={disabledStart}
        type="primary"
        onClick={activeItem.proceed}
      >
        {use_mcq_proctoring ? 'Accept & Start Quiz' :  'Start Quiz'}
      </Button>
    ) : null;

  // Shows a retake button when the mcq can be attempted multiple times
  const RetakeTestBtn =
    activeItem.state === TestState.COMPLETED && !activeItem.one_shot ? (
      <Button
        icon="reload"
        disabled={disabledStart}
        type="primary"
        onClick={activeItem.proceed}
      >
        Retake test
      </Button>
    ) : null;

  const completedBtn =
    !activeItem.passed &&
    activeItem.state === TestState.COMPLETED &&
    activeItem.one_shot ? (
      <Tooltip
        placement="top"
        title={moment(activeItem.submit_time).format("MMM D YYYY, hh:mmA")}
      >
        <Button className={`completed ${classes.completedButton}`}>
          <span className={classes.roundCheck} />
          <span className={classes.buttonItemSpacing}>Completed</span>
        </Button>
      </Tooltip>
    ) : null;

  if (!continueBtn && !startTestBtn && !RetakeTestBtn && !completedBtn)
    return null;

  return (
    <Row className="start-button-actions">
      {continueBtn}
      {startTestBtn}
      {RetakeTestBtn}
      {completedBtn}
    </Row>
  );
};

StartButton.propTypes = {
  activeItem: PropTypes.object,
  disabledStart: PropTypes.any,
  classes: PropTypes.object,
};

StartButtonV1.propTypes = {
  activeItem: PropTypes.object,
  disabledStart: PropTypes.any,
  classes: PropTypes.object,
};

StartMCQModal.propTypes = {
  activeItem: PropTypes.object,
  disabledStart: PropTypes.any,
  classes: PropTypes.object,
  mcq_use_apiv1: PropTypes.bool,
  use_mcq_proctoring: PropTypes.bool,
};

StartMCQModalV2.propTypes = {
  activeItem: PropTypes.object,
  disabledStart: PropTypes.any,
  classes: PropTypes.object,
};

const StyledStartMCQModal = withStyles(useStyles)(StartMCQModal);
const StyledStartMCQModalV2 = withStyles(useStyles)(StartMCQModalV2);

export { 
  StyledStartMCQModal as StartMCQModal,
  StyledStartMCQModalV2 as StartMCQModalV2
};
